import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorPage } from '../components/ui/ErrorPage';
import { Loader } from '../components/ui/Loader';
// A route's chunk loads behind the skeleton of the page it is about to become,
// not behind a centred spinner. SP does the same: the feature's own placeholder
// stands in, so the reader sees the shape of what is coming.
import DetailPageSkeleton from '../components/DetailPage/DetailPageSkeleton';
import ListPageSkeleton from '../components/ListPage/ListPageSkeleton';
import { DETAIL_SKELETON_SHAPES } from '../components/DetailPage/detailSkeletonShapes';
import { Version } from '../components/Version';
import { RootRedirect } from '../components/RootRedirect';

// Lazy load all page components
const SignIn = lazy(() =>
  import('../pages/auth/SignIn').then(module => ({ default: module.SignIn }))
);
const ForgotPasswordPage = lazy(() => import('../pages/auth/ForgotPassword'));
const SignUp = lazy(() => import('../pages/auth/SignUp'));
const Dashboard = lazy(() => import('../pages/dashboard'));
const ProfilePage = lazy(() => import('../pages/profile'));
const JobsPage = lazy(() => import('../pages/jobs'));
const JobDetailsPage = lazy(() => import('../pages/job'));
const ServiceProvidersPage = lazy(() => import('../pages/serviceProviders'));
const ServiceProviderDetailsPage = lazy(() => import('../pages/serviceProvider'));
const EstimatesPage = lazy(() => import('../pages/estimates'));
const EstimateDetailsPage = lazy(() => import('../pages/estimates/estimate-detail'));
const InvoicesPage = lazy(() => import('../pages/invoices'));
const InvoiceDetailsPage = lazy(() => import('../pages/invoice'));
const SettingsPage = lazy(() => import('../pages/settings'));
const InboxPage = lazy(() => import('../pages/inbox'));
const RequestService = lazy(() => import('../pages/RequestService'));
const SetPasswordPage = lazy(() => import('../pages/SetPassword'));
const ResetPasswordPage = lazy(() => import('../pages/ResetPassword'));
const UpdateProfilePage = lazy(() => import('../pages/UpdateProfile'));
const ClientInvitePage = lazy(() => import('../pages/ClientInvite'));
const MagicLinkRedirect = lazy(() => import('../pages/ClientInvite/MagicLinkRedirect'));
const AppointmentsPage = lazy(() => import('../pages/appointments'));
const AppointmentDetailsPage = lazy(() => import('../pages/appointment'));
const AccessLinkPage = lazy(() => import('../pages/AccessLink'));
export interface RouteConfig {
  path: string;
  element: React.ReactNode;
  isPrivate?: boolean;
}

export const publicRoutes: RouteConfig[] = [
  {
    path: '/signin',
    element: (
      <Suspense fallback={<Loader size="xl" className="min-h-screen" />}>
        <SignIn />
      </Suspense>
    ),
    isPrivate: false,
  },
  {
    path: '/signup',
    element: (
      <Suspense fallback={<Loader size="xl" className="min-h-screen" />}>
        <SignUp />
      </Suspense>
    ),
    isPrivate: false,
  },
  {
    path: '/forgot-password',
    element: (
      <Suspense fallback={<Loader size="xl" className="min-h-screen" />}>
        <ForgotPasswordPage />
      </Suspense>
    ),
    isPrivate: false,
  },
  {
    path: '/reset-password',
    element: (
      <Suspense fallback={<Loader size="xl" className="min-h-screen" />}>
        <ResetPasswordPage />
      </Suspense>
    ),
    isPrivate: false,
  },
];

export const privateRoutes: RouteConfig[] = [
  {
    path: '/dashboard',
    element: (
      <Suspense fallback={<Loader size="xl" className="min-h-screen" />}>
        <Dashboard />
      </Suspense>
    ),
    isPrivate: true,
  },
  {
    path: '/profile',
    element: (
      <Suspense fallback={<Loader size="xl" className="min-h-screen" />}>
        <ProfilePage />
      </Suspense>
    ),
    isPrivate: true,
  },
  {
    path: '/jobs',
    element: (
      <Suspense fallback={<ListPageSkeleton />}>
        <JobsPage />
      </Suspense>
    ),
    isPrivate: true,
  },
  {
    path: '/jobs/:id',
    element: (
      <Suspense fallback={<DetailPageSkeleton {...DETAIL_SKELETON_SHAPES.job} />}>
        <JobDetailsPage />
      </Suspense>
    ),
    isPrivate: true,
  },
  {
    path: '/service-providers',
    element: (
      <Suspense fallback={<ListPageSkeleton />}>
        <ServiceProvidersPage />
      </Suspense>
    ),
    isPrivate: true,
  },
  {
    path: '/service-providers/:id',
    element: (
      <Suspense fallback={<DetailPageSkeleton {...DETAIL_SKELETON_SHAPES.serviceProvider} />}>
        <ServiceProviderDetailsPage />
      </Suspense>
    ),
    isPrivate: true,
  },
  {
    path: '/estimates',
    element: (
      <Suspense fallback={<ListPageSkeleton />}>
        <EstimatesPage />
      </Suspense>
    ),
    isPrivate: true,
  },
  {
    path: '/estimates/:id',
    element: (
      <Suspense fallback={<DetailPageSkeleton {...DETAIL_SKELETON_SHAPES.estimate} />}>
        <EstimateDetailsPage />
      </Suspense>
    ),
    isPrivate: true,
  },
  {
    path: '/appointments',
    element: (
      <Suspense fallback={<ListPageSkeleton />}>
        <AppointmentsPage />
      </Suspense>
    ),
    isPrivate: true,
  },
  {
    path: '/appointments/:id',
    element: (
      <Suspense fallback={<DetailPageSkeleton {...DETAIL_SKELETON_SHAPES.appointment} />}>
        <AppointmentDetailsPage />
      </Suspense>
    ),
    isPrivate: true,
  },
  {
    path: '/settings',
    element: (
      <Suspense fallback={<Loader size="xl" className="min-h-screen" />}>
        <SettingsPage />
      </Suspense>
    ),
    isPrivate: true,
  },
  {
    path: '/invoices',
    element: (
      <Suspense fallback={<ListPageSkeleton />}>
        <InvoicesPage />
      </Suspense>
    ),
    isPrivate: true,
  },
  {
    path: '/inbox',
    element: (
      <Suspense fallback={<Loader size="xl" className="min-h-screen" />}>
        <InboxPage />
      </Suspense>
    ),
    isPrivate: true,
  },
  {
    path: '/inbox/:chatId',
    element: (
      <Suspense fallback={<Loader size="xl" className="min-h-screen" />}>
        <InboxPage />
      </Suspense>
    ),
    isPrivate: true,
  },
  {
    path: '/invoices/:id',
    element: (
      <Suspense fallback={<DetailPageSkeleton {...DETAIL_SKELETON_SHAPES.invoice} />}>
        <InvoiceDetailsPage />
      </Suspense>
    ),
    isPrivate: true,
  },
  {
    path: '/request-service',
    element: (
      <Suspense fallback={<Loader size="xl" className="min-h-screen" />}>
        <RequestService />
      </Suspense>
    ),
    isPrivate: true,
  },
];

export const useCommonRoutes = () => {
  const { t } = useTranslation();

  return [
    {
      path: '/',
      element: <RootRedirect />,
    },
    {
      path: '/v',
      element: <Version />,
    },
    {
      path: '/unauthorized',
      element: (
        <ErrorPage title={t('unauthorizedAccess')} message={t('unauthorizedAccessMessage')} />
      ),
    },
    {
      path: '*',
      element: <ErrorPage title={t('pageNotFound')} message={t('pageNotFoundMessage')} />,
    },
    {
      path: '/set-password',
      element: (
        <Suspense fallback={<Loader size="xl" className="min-h-screen" />}>
          <SetPasswordPage />
        </Suspense>
      ),
    },
    {
      path: '/update-profile',
      element: (
        <Suspense fallback={<Loader size="xl" className="min-h-screen" />}>
          <UpdateProfilePage />
        </Suspense>
      ),
    },
    {
      path: '/magic',
      element: (
        <Suspense fallback={<Loader size="xl" className="min-h-screen" />}>
          <MagicLinkRedirect />
        </Suspense>
      ),
    },
    {
      path: '/invite',
      element: (
        <Suspense fallback={<Loader size="xl" className="min-h-screen" />}>
          <ClientInvitePage />
        </Suspense>
      ),
    },
    {
      path: '/accesslink',
      element: (
        <Suspense fallback={<Loader size="xl" className="min-h-screen" />}>
          <AccessLinkPage />
        </Suspense>
      ),
    },
  ];
};
